import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from '@roger/ui-components';
import { PermissionSlug } from '../../constants/featureFlags';
import { useStore } from '../../store';
import {
  listHistoricalClaimCaptures,
  getHistoricalClaimCapture,
  downloadHistoricalClaimPrint,
} from '../../services/api';
import type {
  HistoricalClaimCaptureDetail,
  HistoricalClaimCaptureSummary,
  HistoricalClaimPdf,
} from '../../services/api/types.gen';

const coverage = {
  complete: 'Capture complete',
  partial: 'Incomplete',
  unavailable: 'Unavailable',
  not_attempted: 'Not captured',
};
const stamp = (value: string | null) =>
  value && Number.isFinite(Date.parse(value))
    ? new Date(value).toLocaleString()
    : 'Capture date unavailable';
const gapText = (code: string): string => {
  if (code.includes('print'))
    return 'An original claim PDF is unavailable or could not be verified.';
  if (code.includes('time') || code.includes('date'))
    return 'A source date is missing or could not be verified.';
  if (code.includes('history') || code.includes('record'))
    return 'Some source claim history is missing or could not be verified.';
  if (code.includes('inventory') || code.includes('episode'))
    return 'The source billing inventory is incomplete.';
  return 'Some source evidence is unavailable or could not be verified.';
};

export default function HistoricalClaimHistory({
  patientId,
}: {
  patientId: string;
}) {
  const canView = useStore(
    state =>
      state.user?.agency?.is_rogeremr_enabled === true &&
      state.hasPermission(PermissionSlug.HISTORICAL_CLAIMS_VIEW)
  );
  const agencyId = useStore(state => state.user?.agency?.id);
  return canView ? (
    <History key={`${agencyId}:${patientId}`} patientId={patientId} />
  ) : null;
}

function History({ patientId }: { patientId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section
      className='mb-4 rounded-lg border bg-card'
      aria-label='Historical claims'
    >
      <button
        type='button'
        className='flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-muted/30'
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        <span>
          <span className='block font-medium'>Historical claims</span>
          <span className='block text-xs text-muted-foreground'>
            Retained source billing history and original claim PDFs
          </span>
        </span>
        <span aria-hidden='true'>{open ? '−' : '+'}</span>
      </button>
      {open && <CaptureList patientId={patientId} />}
    </section>
  );
}

function CaptureList({ patientId }: { patientId: string }) {
  const [captures, setCaptures] = useState<HistoricalClaimCaptureSummary[]>([]);
  const [selected, setSelected] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    void listHistoricalClaimCaptures({
      path: { patient_id: patientId },
      query: { limit: 20, offset },
      signal: controller.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (controller.signal.aborted || !data) return;
        setCaptures(previous =>
          offset === 0
            ? data.captures
            : [
                ...previous,
                ...data.captures.filter(
                  item => !previous.some(old => old.id === item.id)
                ),
              ]
        );
        setSelected(previous =>
          offset === 0 && !data.captures.some(item => item.id === previous)
            ? (data.captures[0]?.id ?? '')
            : previous
        );
        setHasMore(data.has_more);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [patientId, offset, revision]);
  const refresh = () => {
    setOffset(0);
    setRevision(value => value + 1);
  };
  return (
    <div className='space-y-4 border-t p-4'>
      <p className='text-sm text-muted-foreground'>
        History across captured episodes. Source billing records do not create
        charges or claims in Roger.
      </p>
      <div className='flex flex-wrap items-end gap-3'>
        {captures.length > 0 && (
          <label className='flex min-w-0 flex-col gap-1 text-sm'>
            Source capture
            <select
              className='max-w-full rounded-md border bg-background px-3 py-2'
              value={selected}
              onChange={event => setSelected(event.target.value)}
            >
              {captures.map(capture => (
                <option key={capture.id} value={capture.id}>
                  {stamp(capture.captured_at)} · {coverage[capture.status]} ·
                  Retained {stamp(capture.retained_at)}
                </option>
              ))}
            </select>
          </label>
        )}
        <Button
          variant='outline'
          size='sm'
          onClick={refresh}
          disabled={loading}
        >
          Refresh history
        </Button>
        {hasMore && (
          <Button
            variant='ghost'
            size='sm'
            disabled={loading}
            onClick={() => {
              setOffset(captures.length);
              setRevision(value => value + 1);
            }}
          >
            Load older captures
          </Button>
        )}
      </div>
      {loading && (
        <p role='status' className='text-sm text-muted-foreground'>
          Loading claim history…
        </p>
      )}
      {error && (
        <div role='alert' className='text-sm text-destructive'>
          Unable to load claim history.{' '}
          <button
            className='underline'
            onClick={() => setRevision(value => value + 1)}
          >
            Try again
          </button>
        </div>
      )}
      {!loading && !error && captures.length === 0 && (
        <p className='text-sm text-muted-foreground'>
          No claim capture has been retained for this patient. This does not
          confirm that the source chart had no claims.
        </p>
      )}
      {selected && (
        <Capture key={selected} patientId={patientId} captureId={selected} />
      )}
    </div>
  );
}

function Capture({
  patientId,
  captureId,
}: {
  patientId: string;
  captureId: string;
}) {
  const [detail, setDetail] = useState<HistoricalClaimCaptureDetail | null>(
    null
  );
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfRequest = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    void getHistoricalClaimCapture({
      path: { patient_id: patientId, capture_id: captureId },
      signal: controller.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (!controller.signal.aborted && data) setDetail(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => controller.abort();
  }, [patientId, captureId, attempt]);
  useEffect(() => () => pdfRequest.current?.abort(), []);
  useEffect(
    () => () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl]
  );
  const openPdf = async (pdf: HistoricalClaimPdf) => {
    if (!pdf.print_id) return;
    pdfRequest.current?.abort();
    const controller = new AbortController();
    pdfRequest.current = controller;
    setPdfError(false);
    setPdfLoading(true);
    try {
      const { data } = await downloadHistoricalClaimPrint({
        path: {
          patient_id: patientId,
          capture_id: captureId,
          print_id: pdf.print_id,
        },
        signal: controller.signal,
        throwOnError: true,
      });
      if (!controller.signal.aborted) {
        if (!(data instanceof Blob)) throw new Error('PDF unavailable');
        setPdfUrl(URL.createObjectURL(data));
      }
    } catch {
      if (!controller.signal.aborted) setPdfError(true);
    } finally {
      if (!controller.signal.aborted) setPdfLoading(false);
    }
  };
  if (error)
    return (
      <p role='alert'>
        Unable to load this capture.{' '}
        <button
          className='underline'
          onClick={() => setAttempt(value => value + 1)}
        >
          Try again
        </button>
      </p>
    );
  if (!detail)
    return (
      <p role='status' className='text-sm text-muted-foreground'>
        Loading source capture…
      </p>
    );
  const gaps = [...new Set((detail.gaps ?? []).map(gapText))];
  return (
    <div className='space-y-4'>
      <div className='border-l-2 border-muted-foreground/30 pl-3 text-sm'>
        <p className='font-medium'>{coverage[detail.status]}</p>
        <p className='text-muted-foreground'>
          Captured: {stamp(detail.captured_at)} · Retained:{' '}
          {stamp(detail.retained_at)}
        </p>
      </div>
      {detail.status !== 'complete' && (
        <div className='rounded-md border border-amber-200 bg-amber-50/50 p-3 text-sm text-amber-950'>
          <p className='font-medium'>
            This capture does not establish complete claim coverage.
          </p>
          {gaps.length > 0 && (
            <ul className='mt-1 list-disc pl-5'>
              {gaps.map(gap => (
                <li key={gap}>{gap}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {(detail.claims ?? []).length === 0 && (
        <p className='text-sm text-muted-foreground'>
          {detail.status === 'complete'
            ? 'External EMR returned no claims for this capture.'
            : 'No verified claim actions are available in this capture.'}
        </p>
      )}
      {(detail.claims ?? []).map(claim => (
        <article key={claim.claim_key} className='rounded-md border'>
          <div className='space-y-2 bg-muted/20 px-4 py-3'>
            <h4 className='text-sm font-medium'>
              Source claim {claim.claim_key}
            </h4>
            <dl className='flex flex-wrap gap-x-6 gap-y-2 text-sm'>
              {claim.inventory_labels.map((label, index) => (
                <div key={`${label}:${index}`}>
                  <dt className='text-xs text-muted-foreground'>{label}</dt>
                  <dd>{claim.inventory_values[index] || 'Not recorded'}</dd>
                </div>
              ))}
            </dl>
            <p className='text-xs text-muted-foreground'>
              Source episode {claim.source_episode_key}
            </p>
          </div>
          <div className='overflow-x-auto'>
            <table className='w-full text-left text-sm'>
              <caption className='sr-only'>
                Source actions for claim {claim.claim_key}
              </caption>
              <thead className='border-y text-xs text-muted-foreground'>
                <tr>
                  <th className='px-4 py-2'>Source date</th>
                  <th className='px-4 py-2'>Action / status</th>
                  <th className='px-4 py-2'>Recorded by</th>
                </tr>
              </thead>
              <tbody>
                {claim.actions.map((action, index) => (
                  <tr
                    key={`${action.transaction_key}:${index}`}
                    className='border-b last:border-0'
                  >
                    <td className='px-4 py-2'>
                      {action.source_date || 'Not recorded'}
                    </td>
                    <td className='px-4 py-2'>
                      {action.action || 'Not recorded'}
                      <span className='block text-xs text-muted-foreground'>
                        {action.status || 'Status not recorded'} · Transaction{' '}
                        {action.transaction_key}
                      </span>
                    </td>
                    <td className='px-4 py-2'>
                      {action.source_author || 'Not recorded'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {claim.actions.length === 0 && (
            <p className='px-4 py-3 text-sm text-muted-foreground'>
              Source action history is unavailable.
            </p>
          )}
          <div className='flex flex-wrap gap-3 border-t px-4 py-3'>
            {claim.prints.map(pdf =>
              pdf.status === 'available' && pdf.print_id ? (
                <Button
                  key={pdf.transaction_key}
                  variant='outline'
                  size='sm'
                  disabled={pdfLoading}
                  onClick={() => void openPdf(pdf)}
                >
                  Open original PDF · {pdf.transaction_key}
                </Button>
              ) : (
                <span
                  key={pdf.transaction_key}
                  className='text-xs text-muted-foreground'
                >
                  Original PDF unavailable · {pdf.transaction_key}
                </span>
              )
            )}
          </div>
        </article>
      ))}
      {Object.keys(detail.inventories ?? {}).length > 0 && (
        <details className='rounded-md border p-3 text-sm'>
          <summary className='cursor-pointer font-medium'>
            Source billing inventories
          </summary>
          <p className='my-2 text-xs text-muted-foreground'>
            All verified episode inventories, including entries whose action
            history was unavailable.
          </p>
          {Object.entries(detail.inventories ?? {}).map(
            ([episode, inventory]) => (
              <div key={episode} className='mb-4 overflow-x-auto'>
                <h4 className='mb-1 text-xs font-medium'>
                  Source episode {episode}
                </h4>
                <table className='w-full text-left text-xs'>
                  <thead>
                    <tr>
                      <th className='p-2'>Source claim</th>
                      {inventory.labels.map((label, index) => (
                        <th key={index} className='p-2'>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(inventory.rows).map(([claim, values]) => (
                      <tr key={claim} className='border-t'>
                        <td className='p-2'>{claim}</td>
                        {Array.isArray(values) &&
                        values.every(value => typeof value === 'string') ? (
                          values.map((value, index) => (
                            <td key={index} className='p-2'>
                              {value || 'Not recorded'}
                            </td>
                          ))
                        ) : (
                          <td className='p-2' colSpan={inventory.labels.length}>
                            Inventory values unavailable
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {Object.keys(inventory.rows).length === 0 && (
                  <p className='text-muted-foreground'>
                    No claims listed in this episode inventory.
                  </p>
                )}
              </div>
            )
          )}
        </details>
      )}
      {pdfLoading && (
        <p role='status' className='text-sm text-muted-foreground'>
          Verifying original PDF…
        </p>
      )}
      {pdfError && (
        <p role='alert' className='text-sm text-destructive'>
          The original PDF could not be opened. Try again; an unavailable or
          unverified file will not be displayed.
        </p>
      )}
      <Dialog
        open={pdfUrl !== null}
        onOpenChange={open => {
          if (!open) setPdfUrl(null);
        }}
      >
        <DialogContent className='max-w-5xl'>
          <DialogHeader>
            <DialogTitle>Original source claim PDF</DialogTitle>
            <DialogDescription>
              Original PDF retained with this source capture.
            </DialogDescription>
          </DialogHeader>
          {pdfUrl && (
            <>
              <a
                href={pdfUrl}
                download='historical-claim.pdf'
                className='text-sm underline'
              >
                Save original PDF
              </a>
              <iframe
                src={pdfUrl}
                title='Original source claim PDF'
                className='h-[70vh] w-full border'
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
